<template>
  <div
    id="content"
    class="flex-column"
  >
    <RBAlertBox
      text="O garantidor informado não é elegível, por favor insira outro garantidor."
      v-if="showInvalidGuarantor"
    />
    <div class="flex-column">
      <p class="title">
        Dados do garantidor
      </p>
      <p class="text">
        O garantidor fornece apoio e segurança, ajudando o estudante a obter melhores condições de financiamento.
      </p>
    </div>
    <form
      id="rb-form"
      class="flex-column"
      @submit.prevent="submit"
    >
      <RbCpf
        v-model="form.cpf"
        label="CPF do garantidor(a)"
        required
        primary
      />
      <RbInput
        v-model="form.birthdate"
        label="Data de nascimento"
        type="date"
        required
        primary
      />
      <RbMoney
        v-model="form.income"
        label="Renda mensal"
        required
        primary
      />
      <RbRadio
        v-model="form.lives_with_the_student"
        name="lives_with_the_student"
        label="O estudante mora com o garantidor?"
        :config="{ key: 'value', title: 'title' }"
        :data="[{ value: 1, title: 'Sim' }, { value: 0, title: 'Não' }]"
        inline
        primary
      />
      <div
        class="address"
        v-if="!form.lives_with_the_student.value"
      >
        <RbCep
          v-model="form.zip_code"
          v-model:state="form.state"
          v-model:city="form.city"
          v-model:neighborhood="form.neighborhood"
          v-model:street="form.street"
          label="CEP"
          seach-cep
          required
          primary
        />
        <RbInput
          v-model="form.state"
          label="Estado"
          required
          primary
          readonly
          :max-length="2"
        />
        <RbInput
          v-model="form.city"
          label="Cidade"
          required
          primary
          readonly
        />
        <RbInput
          v-model="form.neighborhood"
          label="Bairro"
          required
          primary
        />
        <RbInput
          v-model="form.street"
          label="Rua"
          required
          primary
        />
        <RbInput
          v-model.trim="form.number_address"
          label="Número"
          required
          primary
        />
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, reactive, ref, watch } from 'vue';
import RbCep from '../standards/RbCep.vue';
import RbInput from '../standards/RbInput.vue';
import RbCpf from '../standards/RbCpf.vue';
import RbMoney from '../standards/RbMoney.vue';
import RbRadio from '../standards/RbRadio.vue';
import RBAlertBox from '../standards/RBAlertBox.vue';

import { findPerson } from '@/api/Person';
import { guarantorStep } from '@/api/Step';
import { loaderStore, proposalStore, snackbarStore, stepStore } from '@/store/app';

const showInvalidGuarantor = ref(false);
onBeforeMount(async () => {
  try {
    if (proposalStore().getProposal().guarantor_id) {
      loaderStore().setOpen(true);
      const guarantor = await findPerson(proposalStore().getProposal().guarantor_id);
      Object.assign(form, guarantor);
      loaderStore().setOpen(false);
    }
  } catch (error) {
    snackbarStore().alert('Tivemos um problema, tente novamente!');
  } finally {
    loaderStore().setOpen(false);
  }
});

const form = reactive({
  cpf: '',
  birthdate: '',
  income: '',
  lives_with_the_student: { value: 1, title: 'Sim' },
  zip_code: '',
  state: '',
  city: '',
  street: '',
  neighborhood: '',
  number_address: ''
});

watch(() => form.lives_with_the_student, () => {
  if (form.lives_with_the_student.value) {
    Object.assign(form, {
      zip_code: '',
      state: '',
      city: '',
      street: '',
      neighborhood: '',
      number_address: ''
    });
  }
});

async function submit() {
  loaderStore().setOpen(true);
  const data = formatGuarantorRequest();
  const response = await guarantorStep(data);

  if (!response) {
    loaderStore().setOpen(false);
    return snackbarStore().alert('Tivemos um problema para salvar seus dados, tente novamente!');
  }

  showInvalidGuarantor.value = Boolean(response.guarantor);
  if (showInvalidGuarantor.value) return loaderStore().setOpen(false);

  if (response.proposal) {
    proposalStore().setProposal(response.proposal);
    loaderStore().setOpen(false);
    return stepStore().nextStep('feedback');
  }

  snackbarStore().alert('Tivemos um problema para salvar seus dados, tente novamente!');
  loaderStore().setOpen(false);
}

function formatGuarantorRequest() {
  return {
    guarantor: {
      ...form,
      income: String(form.income).replace('R$ ', '').replace(',', '.'),
      lives_with_the_student: form.lives_with_the_student?.value
    },
    proposal: proposalStore().getProposal()
  };
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}

#content {
  margin: 1.3rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .title {
    font-family: Poppins;
    font-size: 1rem;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0em;
    text-align: left;
  }

  .text {
    font-size: 0.90rem;
    font-weight: 400;
    line-height: 1.45rem;
    letter-spacing: 0.025em;
    text-align: left;
    color: rgba(77, 77, 77, 1);
  }

  &>div {
    gap: 16px;
  }

  &>form {
    gap: 25px;

    &>div.address {
      gap: 25px;
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
